import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  TrendingUp, Scissors, Package, Target, Calendar, Store, User,
  ChevronUp, ChevronDown, Download, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmt2 = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const monthOptionsList = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    opts.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
};

export default function AdminBarberSales() {
  const { getAuthHeaders } = useAuth();
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [branchFilter, setBranchFilter] = useState('all');
  const [barberFilter, setBarberFilter] = useState('all');
  const [sortKey, setSortKey] = useState('total_revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [goalDialog, setGoalDialog] = useState(null); // { barber, suggestions }
  const [goalForm, setGoalForm] = useState({ avg_ticket_goal: '', services_goal: '', products_revenue_goal: '', extra_services_goal: '', neurone_revenue_goal: '' });
  const [savingGoals, setSavingGoals] = useState(false);

  const monthOptions = useMemo(monthOptionsList, []);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branches`, getAuthHeaders());
      setBranches(res.data || []);
    } catch (e) {
      // Silent fail; branches filter just stays empty
    }
  }, [getAuthHeaders]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (branchFilter !== 'all') params.append('branch_id', branchFilter);
      const res = await axios.get(`${API}/barbers/sales-summary?${params.toString()}`, getAuthHeaders());
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar ventas por barbero');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, branchFilter, getAuthHeaders]);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const filteredBarbers = useMemo(() => {
    if (!data?.barbers) return [];
    let list = [...data.barbers];
    if (barberFilter !== 'all') list = list.filter(b => b.barber_id === barberFilter);
    list.sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      if (typeof va === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [data, barberFilter, sortKey, sortDir]);

  const totals = useMemo(() => {
    const t = {
      services_count: 0, services_revenue: 0,
      products_count: 0, products_revenue: 0,
      transactions: 0, total_revenue: 0,
    };
    filteredBarbers.forEach(b => {
      t.services_count += b.services_count || 0;
      t.services_revenue += b.services_revenue || 0;
      t.products_count += b.products_count || 0;
      t.products_revenue += b.products_revenue || 0;
      t.transactions += b.transactions || 0;
      t.total_revenue += b.total_revenue || 0;
    });
    t.avg_ticket = t.transactions > 0 ? t.total_revenue / t.transactions : 0;
    return t;
  }, [filteredBarbers]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  const openGoalDialog = (barber) => {
    // Suggest goals slightly above current month performance
    const suggestedAvg = Math.ceil((barber.avg_ticket || 0) * 1.1);
    const suggestedServices = Math.ceil((barber.services_count || 0) * 1.1);
    const suggestedProducts = Math.ceil((barber.products_revenue || 0) * 1.1);
    setGoalDialog({
      barber,
      suggestions: { avg: suggestedAvg, services: suggestedServices, products: suggestedProducts }
    });
    setGoalForm({
      avg_ticket_goal: String(barber.goals?.avg_ticket || ''),
      services_goal: String(barber.goals?.services || ''),
      products_revenue_goal: String(barber.goals?.products_revenue || ''),
    });
  };

  const applySuggestions = () => {
    if (!goalDialog) return;
    setGoalForm({
      avg_ticket_goal: String(goalDialog.suggestions.avg),
      services_goal: String(goalDialog.suggestions.services),
      products_revenue_goal: String(goalDialog.suggestions.products),
    });
  };

  const saveGoals = async () => {
    if (!goalDialog) return;
    setSavingGoals(true);
    try {
      await axios.put(
        `${API}/barbers/${goalDialog.barber.barber_id}/goals`,
        {
          avg_ticket_goal: parseFloat(goalForm.avg_ticket_goal) || 0,
          services_goal: parseInt(goalForm.services_goal) || 0,
          products_revenue_goal: parseFloat(goalForm.products_revenue_goal) || 0,
        },
        getAuthHeaders()
      );
      toast.success('Metas actualizadas');
      setGoalDialog(null);
      fetchSummary();
    } catch (e) {
      toast.error('Error al guardar metas');
    } finally {
      setSavingGoals(false);
    }
  };

  const exportCSV = () => {
    if (!filteredBarbers.length) return;
    const headers = ['Barbero', 'Sucursal', 'Tipo', 'Servicios #', 'EXTRA #', 'Servicios $', 'Productos #', 'Productos $', 'NEURONE $', 'Transacciones', 'Ticket Prom.', 'Total', 'Meta Ticket', 'Meta Servicios', 'Meta Productos $', 'Meta EXTRA #', 'Meta NEURONE $'];
    const rows = filteredBarbers.map(b => [
      b.name,
      b.branch_name || '',
      b.role_type === 'stylist' ? 'Estilista' : 'Barbero',
      b.services_count,
      b.extra_services_count || 0,
      b.services_revenue,
      b.products_count,
      b.products_revenue,
      b.neurone_revenue || 0,
      b.transactions,
      b.avg_ticket,
      b.total_revenue,
      b.goals?.avg_ticket || 0,
      b.goals?.services || 0,
      b.goals?.products_revenue || 0,
      b.goals?.extra_services || 0,
      b.goals?.neurone_revenue || 0,
    ]);
    const csv = [headers, ...rows].map(r => r.map(x => `"${x}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventas-barberos-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-[1400px]" data-testid="admin-barber-sales">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight flex items-center gap-3">
              <TrendingUp className="w-7 h-7 text-gold" />
              Ventas por Barbero
            </h1>
            <p className="text-zinc-500 mt-1">
              Análisis histórico de ventas de servicios y productos para determinar metas personales
            </p>
          </div>
          <Button
            variant="outline"
            onClick={exportCSV}
            disabled={!filteredBarbers.length}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="filters-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-zinc-400 flex items-center gap-1.5 mb-1.5">
                <Calendar className="w-3 h-3" /> Mes
              </Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(o => (
                    <SelectItem key={o.value} value={o.value} data-testid={`month-option-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 flex items-center gap-1.5 mb-1.5">
                <Store className="w-3 h-3" /> Sucursal
              </Label>
              <Select value={branchFilter} onValueChange={(v) => { setBranchFilter(v); setBarberFilter('all'); }}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="branch-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="branch-option-all">Todas las sucursales</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 flex items-center gap-1.5 mb-1.5">
                <User className="w-3 h-3" /> Barbero
              </Label>
              <Select value={barberFilter} onValueChange={setBarberFilter}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="barber-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="barber-option-all">Todos los barberos</SelectItem>
                  {(data?.barbers || []).map(b => (
                    <SelectItem key={b.barber_id} value={b.barber_id} data-testid={`barber-option-${b.barber_id}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            icon={<Scissors className="w-4 h-4 text-green-400" />}
            label="Ventas Servicios"
            value={fmt(totals.services_revenue)}
            sub={`${totals.services_count} servicios`}
            testId="summary-services"
          />
          <SummaryCard
            icon={<Package className="w-4 h-4 text-blue-400" />}
            label="Ventas Productos"
            value={fmt(totals.products_revenue)}
            sub={`${totals.products_count} productos`}
            testId="summary-products"
          />
          <SummaryCard
            icon={<TrendingUp className="w-4 h-4 text-gold" />}
            label="Total Ingresos"
            value={fmt(totals.total_revenue)}
            sub={`${totals.transactions} transacciones`}
            testId="summary-total"
          />
          <SummaryCard
            icon={<Target className="w-4 h-4 text-purple-400" />}
            label="Ticket Promedio"
            value={fmt2(totals.avg_ticket)}
            sub="Promedio global"
            testId="summary-avg-ticket"
          />
        </div>

        {/* Table */}
        <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden" data-testid="barbers-table-card">
          {loading ? (
            <div className="p-12 text-center text-zinc-500">Cargando datos...</div>
          ) : filteredBarbers.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              Sin ventas registradas para los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="barber-sales-table">
                <thead className="bg-zinc-950/60 border-b border-zinc-800">
                  <tr>
                    <Th onClick={() => toggleSort('name')}>Barbero {sortIcon('name')}</Th>
                    <Th onClick={() => toggleSort('branch_name')}>Sucursal {sortIcon('branch_name')}</Th>
                    <Th onClick={() => toggleSort('services_count')} className="text-right">
                      Servicios # {sortIcon('services_count')}
                    </Th>
                    <Th onClick={() => toggleSort('extra_services_count')} className="text-right">
                      EXTRA # {sortIcon('extra_services_count')}
                    </Th>
                    <Th onClick={() => toggleSort('services_revenue')} className="text-right">
                      Servicios $ {sortIcon('services_revenue')}
                    </Th>
                    <Th onClick={() => toggleSort('products_count')} className="text-right">
                      Productos # {sortIcon('products_count')}
                    </Th>
                    <Th onClick={() => toggleSort('products_revenue')} className="text-right">
                      Productos $ {sortIcon('products_revenue')}
                    </Th>
                    <Th onClick={() => toggleSort('neurone_revenue')} className="text-right">
                      NEURONE $ {sortIcon('neurone_revenue')}
                    </Th>
                    <Th onClick={() => toggleSort('transactions')} className="text-right">
                      Trans. {sortIcon('transactions')}
                    </Th>
                    <Th onClick={() => toggleSort('avg_ticket')} className="text-right">
                      Ticket Prom. {sortIcon('avg_ticket')}
                    </Th>
                    <Th onClick={() => toggleSort('total_revenue')} className="text-right">
                      Total $ {sortIcon('total_revenue')}
                    </Th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Metas
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {filteredBarbers.map((b) => (
                    <tr key={b.barber_id} className="hover:bg-zinc-900/40 transition-colors" data-testid={`row-barber-${b.barber_id}`}>
                      <td className="px-3 py-3 text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-300">
                            {b.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{b.name}</p>
                            <p className="text-[10px] text-zinc-500">
                              {b.role_type === 'stylist' ? 'Estilista' : 'Barbero'}
                              {b.exclude_from_ranking && ' · Excluido ranking'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-zinc-400">{b.branch_name || '—'}</td>
                      <td className="px-3 py-3 text-right text-zinc-300">{b.services_count}</td>
                      <td className="px-3 py-3 text-right text-orange-400 font-medium">{b.extra_services_count || 0}</td>
                      <td className="px-3 py-3 text-right text-green-400 font-medium">{fmt(b.services_revenue)}</td>
                      <td className="px-3 py-3 text-right text-zinc-300">{b.products_count}</td>
                      <td className="px-3 py-3 text-right text-blue-400 font-medium">{fmt(b.products_revenue)}</td>
                      <td className="px-3 py-3 text-right text-purple-400 font-medium">{fmt(b.neurone_revenue || 0)}</td>
                      <td className="px-3 py-3 text-right text-zinc-300">{b.transactions}</td>
                      <td className="px-3 py-3 text-right text-purple-300">{fmt2(b.avg_ticket)}</td>
                      <td className="px-3 py-3 text-right text-gold font-bold">{fmt(b.total_revenue)}</td>
                      <td className="px-3 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openGoalDialog(b)}
                          className="border-zinc-700 text-zinc-300 hover:bg-gold/10 hover:text-gold hover:border-gold/50 text-xs h-7"
                          data-testid={`set-goals-btn-${b.barber_id}`}
                        >
                          <Target className="w-3 h-3 mr-1" />
                          Metas
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-zinc-950/80 border-t-2 border-zinc-700">
                  <tr data-testid="totals-row">
                    <td colSpan={2} className="px-3 py-3 text-white font-semibold">TOTALES</td>
                    <td className="px-3 py-3 text-right text-zinc-300 font-semibold">{totals.services_count}</td>
                    <td className="px-3 py-3 text-right text-orange-400 font-semibold">{filteredBarbers.reduce((s, x) => s + (x.extra_services_count || 0), 0)}</td>
                    <td className="px-3 py-3 text-right text-green-400 font-bold">{fmt(totals.services_revenue)}</td>
                    <td className="px-3 py-3 text-right text-zinc-300 font-semibold">{totals.products_count}</td>
                    <td className="px-3 py-3 text-right text-blue-400 font-bold">{fmt(totals.products_revenue)}</td>
                    <td className="px-3 py-3 text-right text-purple-400 font-bold">{fmt(filteredBarbers.reduce((s, x) => s + (x.neurone_revenue || 0), 0))}</td>
                    <td className="px-3 py-3 text-right text-zinc-300 font-semibold">{totals.transactions}</td>
                    <td className="px-3 py-3 text-right text-purple-300 font-semibold">{fmt2(totals.avg_ticket)}</td>
                    <td className="px-3 py-3 text-right text-gold font-bold text-base">{fmt(totals.total_revenue)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Goals Dialog */}
      <Dialog open={!!goalDialog} onOpenChange={(o) => !o && setGoalDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="goals-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-gold" />
              Definir metas — {goalDialog?.barber?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Define las metas mensuales personales del colaborador basándote en su histórico.
            </DialogDescription>
          </DialogHeader>

          {goalDialog && (
            <div className="space-y-4">
              {/* Historical context */}
              <div className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-3 space-y-1.5 text-xs">
                <p className="text-zinc-400 font-medium mb-1.5">Histórico del mes seleccionado:</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-zinc-500">Ticket prom.</p>
                    <p className="text-purple-300 font-semibold">{fmt2(goalDialog.barber.avg_ticket)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Servicios</p>
                    <p className="text-green-400 font-semibold">{goalDialog.barber.services_count}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Productos $</p>
                    <p className="text-blue-400 font-semibold">{fmt(goalDialog.barber.products_revenue)}</p>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applySuggestions}
                className="w-full border-gold/40 text-gold hover:bg-gold/10"
                data-testid="apply-suggestions-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Sugerir metas (+10% sobre histórico)
              </Button>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-400">Meta de Ticket Promedio ($)</Label>
                  <Input
                    type="number"
                    value={goalForm.avg_ticket_goal}
                    onChange={(e) => setGoalForm({ ...goalForm, avg_ticket_goal: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Ej. 250"
                    data-testid="goal-avg-ticket-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Meta de Servicios (cantidad/mes)</Label>
                  <Input
                    type="number"
                    value={goalForm.services_goal}
                    onChange={(e) => setGoalForm({ ...goalForm, services_goal: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Ej. 120"
                    data-testid="goal-services-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Meta de Productos ($/mes)</Label>
                  <Input
                    type="number"
                    value={goalForm.products_revenue_goal}
                    onChange={(e) => setGoalForm({ ...goalForm, products_revenue_goal: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Ej. 5000"
                    data-testid="goal-products-input"
                  />
                </div>
                {/* Extra + Neurone goals */}
                <div className="pt-2 border-t border-zinc-800 space-y-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Metas por categoría</p>
                  <div>
                    <Label className="text-xs text-orange-400 flex items-center gap-1.5">
                      Meta Servicios EXTRA (cantidad/mes)
                      <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400 uppercase">Extra</span>
                    </Label>
                    <Input
                      type="number"
                      value={goalForm.extra_services_goal}
                      onChange={(e) => setGoalForm({ ...goalForm, extra_services_goal: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 text-white"
                      placeholder="Ej. 30"
                      data-testid="goal-extra-services-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-purple-400 flex items-center gap-1.5">
                      Meta Productos NEURONE ($/mes)
                      <span className="text-[8px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400 uppercase">Neurone</span>
                    </Label>
                    <Input
                      type="number"
                      value={goalForm.neurone_revenue_goal}
                      onChange={(e) => setGoalForm({ ...goalForm, neurone_revenue_goal: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 text-white"
                      placeholder="Ej. 2000"
                      data-testid="goal-neurone-revenue-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setGoalDialog(null)}
              className="text-zinc-400"
              data-testid="cancel-goals-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={saveGoals}
              disabled={savingGoals}
              className="bg-gold text-black hover:bg-gold/90"
              data-testid="save-goals-btn"
            >
              {savingGoals ? 'Guardando...' : 'Guardar metas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Th({ children, onClick, className = '' }) {
  return (
    <th
      onClick={onClick}
      className={`px-3 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider cursor-pointer hover:text-white select-none ${className}`}
    >
      {children}
    </th>
  );
}

function SummaryCard({ icon, label, value, sub, testId }) {
  return (
    <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2">
        {icon}
        {label}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-[10px] text-zinc-500 mt-1">{sub}</p>
    </Card>
  );
}
